import { createApi, fetchBaseQuery } from '@reduxjs/toolkit/query/react';
import { DownloadsParams, ExportItem } from '@/lib/types/setting.types';
import { CACHE_DURATIONS, POLLING_INTERVALS, COMMON_TAGS, providesItem, providesList, invalidatesList, transformErrorResponse } from './baseApi';


// ========================================
// RTK QUERY API DEFINITION
// ========================================

/**
 * Export API with intelligent caching and real-time updates
 * 
 * Tag Types:
 * - Downloads: For download logs data
 * - Exports: For export history data
 * 
 * Cache Strategy:
 * - Download logs: Short cache (2 minutes) as they change frequently
 * - Export operations: Medium cache as they are less frequent
 */
// @ts-ignore
export const exportApi = createApi({
  reducerPath: 'exportApi',
  baseQuery: fetchBaseQuery({ baseUrl: '' }),
  tagTypes: [COMMON_TAGS.EXPORTS, 'Downloads'],
  refetchOnFocus: false,
  refetchOnReconnect: true,
  keepUnusedDataFor: CACHE_DURATIONS.SHORT,
  endpoints: (builder) => ({

    getDownloads: builder.query<ExportItem[], DownloadsParams>({
      query: ({ userId, limit, start }) => `/api/user/get/download/logs/${userId}/${limit}/${start}`,
      keepUnusedDataFor: CACHE_DURATIONS.SHORT,
      // @ts-ignore
      providesTags: (result, error, { userId }) => {
        return result
          ? [
              { type: 'Downloads' as const, id: 'LIST' },
              { type: 'Downloads' as const, id: userId },
              ...result.map(({ _id }) => ({ type: 'Downloads' as const, id: _id })),
            ]
          : [{ type: 'Downloads' as const, id: 'LIST' }];
      },
      transformResponse: (response: any): ExportItem[] => {
        return Array.isArray(response) ? response : [];
      },
      transformErrorResponse,
    }),

    getDownloadsRealtime: builder.query<ExportItem[], DownloadsParams>({
      query: ({ userId, limit, start }) => `/api/user/get/download/logs/${userId}/${limit}/${start}`,
      keepUnusedDataFor: CACHE_DURATIONS.VERY_SHORT,
      // @ts-ignore
      pollingInterval: POLLING_INTERVALS.MODERATE,
      refetchOnFocus: true,
      refetchOnReconnect: true,
      // @ts-ignore
      providesTags: (result, error, { userId }) => {
        return result
          ? [
              { type: 'Downloads' as const, id: 'LIST' },
              { type: 'Downloads' as const, id: userId },
              ...result.map(({ _id }) => ({ type: 'Downloads' as const, id: _id })),
            ]
          : [{ type: 'Downloads' as const, id: 'LIST' }];
      },
      transformResponse: (response: any): ExportItem[] => {
        return Array.isArray(response) ? response : [];
      },
      transformErrorResponse,
    }),

    getDownloadById: builder.query<ExportItem, string>({
      query: (downloadId) => `/api/downloads/${downloadId}`,
      keepUnusedDataFor: CACHE_DURATIONS.MEDIUM,
      // @ts-ignore
      providesTags: (result, error, downloadId) =>
        providesItem('Downloads', downloadId),
      transformErrorResponse,
    }),

    // ========================================
    // EXPORT OPERATIONS (Mutations)
    // ========================================

    requestExport: builder.mutation<
      { message: string; exportId?: string },
      { userId: string; exportType: string; filters?: any }
    >({
      query: ({ userId, exportType, filters }) => ({
        url: `/api/exports/request`,
        method: 'POST',
        body: { userId, exportType, filters },
      }),
      invalidatesTags: (result, error, { userId }) => [
        { type: 'Downloads', id: 'LIST' },
        { type: 'Downloads', id: userId },
        { type: COMMON_TAGS.EXPORTS, id: 'LIST' },
      ],
      transformErrorResponse,
    }),

    cancelExport: builder.mutation<
      { message: string },
      { exportId: string; userId: string }
    >({
      query: ({ exportId }) => ({
        url: `/api/exports/${exportId}/cancel`,
        method: 'PUT',
      }),
      invalidatesTags: (result, error, { userId, exportId }) => [
        { type: 'Downloads', id: 'LIST' },
        { type: 'Downloads', id: userId },
        { type: 'Downloads', id: exportId },
      ],
      transformErrorResponse,
    }),

    deleteExport: builder.mutation<
      { message: string },
      { exportId: string; userId: string }
    >({
      query: ({ exportId }) => ({
        url: `/api/exports/${exportId}`,
        method: 'DELETE',
      }),
      invalidatesTags: (result, error, { userId, exportId }) => [
        { type: 'Downloads', id: 'LIST' },
        { type: 'Downloads', id: userId },
        { type: 'Downloads', id: exportId },
      ],
      transformErrorResponse,
    }),

    exportContactsCsv: builder.mutation<any, { userId: string; payload: {
      firstname: string;
      lastname: string;
      primary_email: string;
      cntid_list: string[];
      pageFlag: string;
      guessedEmail: boolean;
    } }>({
      query: ({ userId, payload }) => ({
        url: `/api/saved-contacts/export`,
        method: "POST",
        body: payload,
        headers: { "content-type": "application/json" },
      }),
      transformResponse: (response: any) => response,
      transformErrorResponse,
    }),

    exportCompanies: builder.mutation<any, { userId: string; payload: {
    firstname: string;
    lastname: string;
    primary_email: string;
    cmpid_list: string[];
    pageFlag: string;
} }>({
      query: ({ userId, payload }) => ({
        url: `/api/saved-companies/export`,
        method: "POST",
        body: { ...payload },
        headers: { "content-type": "application/json" },
      }),
      transformResponse: (response: any) => response,
      transformErrorResponse,
    }),

    bulkExportData: builder.mutation<any, { userId: string }>({
      query: ({ userId }) => ({
        url: `/api/search/bulkExportData/${userId}`,
        method: 'GET',
        headers: { 'content-type': 'application/json' },
      }),
      transformResponse: (response: any) => response,
      transformErrorResponse,
    }),

    refreshDownloads: builder.mutation<void, string>({
      query: () => `/api/exports/refresh`,
      async onCacheEntryAdded(_, { dispatch, cacheDataLoaded }) {
        await cacheDataLoaded;
        dispatch(exportApi.util.invalidateTags([
          { type: 'Downloads', id: 'LIST' },
          { type: 'Downloads', id: 'LIST' }
        ]));
      },
    }),

    prefetchDownloads: builder.query<ExportItem[], DownloadsParams>({
      query: ({ userId, limit, start }) => `/api/user/get/download/logs/${userId}/${limit}/${start}`,
      keepUnusedDataFor: CACHE_DURATIONS.SHORT,
    }),
  }),
}) as any;

// ========================================
// GENERATED HOOKS (Auto-exported)
// ========================================

/**
 * Generated hooks for use in React components
 * They provide automatic loading states, error handling, and cache management
 */
export const {
  // Query hooks
  useGetDownloadsQuery,
  useGetDownloadsRealtimeQuery,
  useGetDownloadByIdQuery,
  usePrefetchDownloadsQuery,

  // Lazy query hooks (for manual triggering)
  useLazyGetDownloadsQuery,
  useLazyGetDownloadByIdQuery,

  // Mutation hooks
  useRequestExportMutation,
  useCancelExportMutation,
  useDeleteExportMutation,
  useRefreshDownloadsMutation,
  useExportContactsCsvMutation,
  useBulkExportDataMutation,
  useExportCompaniesMutation,

  // Utility hooks
  util: {
    getRunningQueriesThunk,
    invalidateTags,
    prefetch,
    resetApiState,
  },
} = exportApi;

// ========================================
// UTILITY FUNCTIONS FOR DEVELOPERS
// ========================================

/**
 * Helper function to check if an export item is expired
 */
export function isExportExpired(exportItem: ExportItem): boolean {
  if (exportItem.isExpired) return true;

  const requestedDate = new Date(exportItem.requestedDate);
  const expiryDate = new Date(requestedDate.getTime() + (exportItem.validDays * 24 * 60 * 60 * 1000));
  return new Date() > expiryDate;
}

/**
 * Helper function to get export status
 */
export function getExportStatus(exportItem: ExportItem): 'expired' | 'active' | 'pending' {
  if (isExportExpired(exportItem)) return 'expired';
  if (exportItem.downloadLink) return 'active';
  return 'pending';
}

/**
 * Helper function to format export type display name
 */
export function formatExportType(type: string): string {
  const typeMap: Record<string, string> = {
    'contacts': 'Contacts Export',
    'companies': 'Companies Export',
    'leads': 'Leads Export',
    'campaigns': 'Campaigns Export',
  };

  return typeMap[type] || type.charAt(0).toUpperCase() + type.slice(1);
}

/**
 * Helper function to warm up export caches for a user
 * Call this when user navigates to exports page for better UX
 */
export function warmupExportCaches(userId: string, dispatch: any, limit: number = 25, start: number = 0) {
  // Prefetch download data in background
  dispatch(exportApi.endpoints.getDownloads.initiate({ userId, limit, start }));
}

/**
 * Helper function to setup real-time export updates
 * Returns cleanup function to stop polling
 */
export function setupRealtimeExports(
  userId: string,
  dispatch: any,
  limit: number = 25,
  start: number = 0,
  interval: number = 300000
) {
  // Start polling query
  const subscription = dispatch(
    exportApi.endpoints.getDownloadsRealtime.initiate(
      { userId, limit, start },
      { forceRefetch: true }
    )
  );

  // Return cleanup function
  return () => {
    subscription.unsubscribe();
  };
}

// ========================================
// EXPORTS FOR OTHER DEVELOPERS
// ========================================

// Export the API itself for advanced usage
export default exportApi;

/**
 * Usage patterns for different scenarios:
 *
 * 1. Standard download logs (refreshes every 2 minutes):
 *    const { data: downloads } = useGetDownloadsQuery({ userId, limit: 25, start: 0 });
 *
 * 2. Real-time download logs (with polling):
 *    const { data: downloads } = useGetDownloadsRealtimeQuery({ userId, limit: 25, start: 0 });
 *
 * 3. Manual refresh:
 *    const [refreshDownloads] = useRefreshDownloadsMutation();
 *    await refreshDownloads(userId);
 *
 * 4. Request new export:
 *    const [requestExport] = useRequestExportMutation();
 *    await requestExport({ userId, exportType: 'contacts', filters: {...} });
 *
 * 5. Delete export:
 *    const [deleteExport] = useDeleteExportMutation();
 *    await deleteExport({ exportId, userId });
 */